import uniqBy from 'lodash.uniqby'
import type { Filters } from '#imports'
import {
  filtersToQuery,
  ref,
  useRouter,
  useState,
  useSupabaseClient,
  useToast,
} from '#imports'
import type { Tables } from '~~/types/database.types.ts'

interface Spot extends Tables<'spots'> {
  categories: Tables<'categories'>
  cities: Tables<'cities'>
  map_id: {
    title: string
    current_slug: string
  }
}

type SpotBookmark = Tables<'spot_bookmarks'> & {
  spot: Spot
}

type Map = Tables<'map_bookmarks'> & {
  spot: Tables<'spots'> &
    {
      categories: Tables<'categories'>
      cities: Tables<'cities'>
    }[]
}

type MapBookmark = Tables<'map_bookmarks'> & {
  map: Map
}

export const useAtlas = () => {
  const spotBookmarks = useState<Spot[]>('spotBookmarks', () => [])
  const spotCategories = useState('spotCategories', () => [])
  const spotDestinations = useState('spotDestinations', () => [])
  const spotCountries = useState('spotCountries', () => [])
  const spotCountriesFilter = useState('spotCountiresFilter', () => [])
  const spotSearch = useState('spotSearch', () => '')
  const filteredSpots = useState<Spot[]>('filteredSpots', () => [])
  const mapBookmarks = useState<Map[]>('mapBookmarks', () => [])
  const atlasMarkers = useState('atlasMarkers', () => [])
  const mapSearch = useState('mapSearch', () => '')
  const mapCategories = useState('mapCategories', () => [])
  const mapDestinations = useState('mapDestinations', () => [])
  const mapCountries = useState('mapCountries', () => [])
  const mapCountriesFilter = useState('mapCountiresFilter', () => [])
  const filteredMaps = useState<Map[]>('filteredMaps', () => [])
  const mapCategoryFilter = useState<Tables<'categories'>[]>(
    'mapCategoryFilter',
    () => [],
  )
  const mapDestinationFilter = useState<Tables<'cities'>[]>(
    'mapDestinationFilter',
    () => [],
  )
  const spotDestinationFilter = useState<Tables<'cities'>[]>(
    'spotDestinationFilter',
    () => [],
  )
  const spotCategoryFilter = useState<Tables<'categories'>[]>(
    'spotCategoryFilter',
    () => [],
  )
  const hightlightSpot = useState<Spot>('highlightSpot', () => null)
  const isMapView = useState('isMapView', () => false)

  const supabase = useSupabaseClient()
  const { showError } = useToast()

  const fetchSpots = async (
    filters?: Filters,
    search?: string,
    limit?: number,
  ) => {
    let query = supabase.from('spot_bookmarks').select(`
        *,
        spot:spots!inner(
          *,
          map_slug:map_id!inner(
            current_slug
          ),
          country_name:cities_id!inner (
            country
          ),
          categories:category_id (
            id,
            name,
            icon
          ),
          cities:cities_id (
            id,
            name,
            country
          ),
          maps:map_id (
            title,
            current_slug,
            profiles:user_id (
              id,
              mappr_handle,
              display_name,
              profile_image_url
            )
          )
        )
      `)
    query = filtersToQuery(
      {
        ...filters,
        search,
        searchReferences: 'spot.fts',
        limit,
      },
      query,
    )

    query.order('created_at', { ascending: false })

    const { data, error } = await query
    if (!data) {
      spotBookmarks.value = []
      spotCategories.value = []
      spotDestinations.value = []
      spotCountries.value = []
      filteredSpots.value = []
      return
    }

    if (error) showError('Could not fetch your saved Spots')

    const spots: Spot[] = data?.map((bookmark: SpotBookmark) => ({
      ...bookmark.spot,
    }))

    const categories: Tables<'categories'>[] = []
    const destinations: Tables<'cities'>[] = []
    const countries: string[] = []

    data?.map((bookmark: SpotBookmark) => {
      categories.push(bookmark.spot.categories)
      destinations.push(bookmark.spot.cities)
      countries.push(bookmark.spot.cities.country)
    })

    spotCategories.value = uniqBy(categories.filter(Boolean), 'id')
    spotDestinations.value = uniqBy(destinations.filter(Boolean), 'id')
    spotCountries.value = uniqBy(destinations.filter(Boolean), 'country').map(
      (country: Tables<'cities'>) => {
        return {
          label: country.country,
          value: country.country,
        }
      },
    )

    spotBookmarks.value = spots
    filteredSpots.value = spots
    setAtlasMarkers(spots)
  }

  const fetchMaps = async (
    filters?: Filters,
    search?: string,
    limit?: number,
  ) => {
    let query = supabase.from('map_bookmarks').select(`
      *,
      map:map_id!inner(
        *,
        profiles:user_id!inner(
          id,
          summary,
          mappr_handle,
          profile_image_url,
          display_name,
          maps_count:maps(count)
        ),
        spots:spots!inner(
          *,
          map_slug:map_id!inner(
            current_slug
          ),
          country_name:cities_id!inner (
            country
          ),
          categories:category_id (
            id,
            name,
            icon
          ),
          cities:cities_id (
            id,
            name,
            country
          )
        )
      )
    `)
    query = filtersToQuery(
      {
        ...filters,
        search,
        searchReferences: 'map.fts',
        limit,
      },
      query,
    )

    query.order('created_at', { ascending: false })

    const { data, error } = await query
    if (!data) {
      mapBookmarks.value = []
      mapCategories.value = []
      mapDestinations.value = []
      mapCountries.value = []
      filteredMaps.value = []
      return
    }

    if (error) showError('Could not fetch your saved Maps')

    const maps: Map[] = data?.map((bookmark: MapBookmark) => ({
      ...bookmark.map,
    }))

    const categories: Tables<'categories'>[] = []
    const destinations: Tables<'cities'>[] = []
    const countries: string[] = []

    // if (!filters) {
    data?.map((bookmark: MapBookmark) => {
      const mapCategories = bookmark.map.spots.map(spot => spot.categories)
      categories.push(...mapCategories)
      const mapDestinations = bookmark.map.spots.map(spot => spot.cities)
      destinations.push(...mapDestinations)
      const mapCountries = bookmark.map.spots.map(spot => spot.country_name)
      countries.push(...mapCountries)
    })

    if (mapCategoryFilter.value.length === 0) {
      mapCategories.value = uniqBy(categories.filter(Boolean), 'id')
    }

    mapDestinations.value = uniqBy(destinations.filter(Boolean), 'id')

    if (mapCountriesFilter.value.length === 0) {
      mapCountries.value = uniqBy(countries.filter(Boolean), 'country').map(
        (country: Tables<'cities'>) => {
          return {
            label: country.country,
            value: country.country,
          }
        },
      )
    }
    const spots: Tables<Spot>[] = []

    maps.map((map: Map) => {
      map.spots.map(spot => {
        spots.push(spot)
      })
    })

    mapBookmarks.value = maps
    filteredMaps.value = maps
    setAtlasMarkers(spots)
  }

  const fetchRecentSpots = () => {
    const router = useRouter()
  }

  const filterSpots = (
    categoryFilter: string[],
    destinationFilter: string[],
  ) => {
    if (!categoryFilter.length && !destinationFilter.length) {
      filteredSpots.value = spotBookmarks.value
      setAtlasMarkers(spotBookmarks.value)
      return
    }
    filteredSpots.value = spotBookmarks.value.filter(bookmark => {
      const matchesCategory =
        !categoryFilter.length ||
        categoryFilter.some(category => category === bookmark.category_id)
      const matchesDestination =
        !destinationFilter.length ||
        destinationFilter.some(
          destination => destination === bookmark.cities_id,
        )
      return matchesCategory && matchesDestination
    })

    setAtlasMarkers(filteredSpots.value)
  }

  // const fetchMaps = async () => {
  //   const { data } = await supabase
  //     .from('map_bookmarks')
  //     .select(`
  //       *,
  //       map:map_id!inner(
  //         *,
  //         profiles:user_id!inner(
  //           id,
  //           summary,
  //           mappr_handle,
  //           profile_image_url,
  //           display_name,
  //           maps_count:maps(count)
  //         ),
  //         spot:spots!inner(
  //           *,
  //           categories:category_id (
  //             id,
  //             name,
  //             icon
  //           ),
  //           cities:cities_id (
  //             id,
  //             name
  //           )
  //         )
  //       )
  //     `)
  //     .eq('map.is_published', true)

  //   const maps: Map = data?.map((bookmark: MapBookmark) => ({
  //     ...bookmark.map,
  //   }))

  //   const spots: Tables<Spot>[] = []
  //   maps.map((map: Map) => {
  //     map.spot.map(spot => {
  //       spots.push(spot)
  //     })
  //   })

  //   const categories: Tables<'categories'>[] = []
  //   const destinations: Tables<'cities'>[] = []

  // spots?.map(spot => {
  //   categories.push(spot.categories)
  //   destinations.push(spot.cities)
  // })

  //   mapCategories.value = uniqBy(categories.filter(Boolean), 'id')
  //   mapDestinations.value = uniqBy(destinations.filter(Boolean), 'id')
  //   setAtlasMarkers(spots)
  //   mapBookmarks.value = maps
  //   filteredMaps.value = maps
  // }

  const filterMaps = (
    categoryFilter: string[],
    destinationFilter: string[],
  ) => {
    if (!categoryFilter.length && !destinationFilter.length) {
      filteredMaps.value = mapBookmarks.value
      atlasMarkers.value = mapBookmarks.value.flatMap(map => map.spot)
      return
    }

    filteredMaps.value = mapBookmarks.value.filter(map => {
      return map.spot.some(spot => {
        const matchesCategory =
          !categoryFilter.length ||
          categoryFilter.some(category => category === spot.category_id)
        const matchesDestination =
          !destinationFilter.length ||
          destinationFilter.some(destination => destination === spot.cities_id)
        return matchesCategory && matchesDestination
      })
    })

    const filteredMarkers = filteredMaps.value
      .flatMap(map => map.spot)
      .filter(spot => {
        const matchesCategory =
          !categoryFilter.length ||
          categoryFilter.some(category => category === spot.category_id)
        const matchesDestination =
          !destinationFilter.length ||
          destinationFilter.some(destination => destination === spot.cities_id)
        return matchesCategory && matchesDestination
      })

    setAtlasMarkers(filteredMarkers)
  }

  const setAtlasMarkers = newMarkers => {
    atlasMarkers.value = newMarkers
  }

  const centerToSpot = (spotId: string) => {
    hightlightSpot.value = spotId
  }

  return {
    spotBookmarks,
    spotCategories,
    spotDestinations,
    filteredSpots,
    filterSpots,
    hightlightSpot,
    centerToSpot,
    mapBookmarks,
    fetchRecentSpots,
    filteredMaps,
    atlasMarkers,
    setAtlasMarkers,
    mapCategories,
    mapDestinations,
    isMapView,
    spotSearch,
    spotCountries,
    spotCountriesFilter,
    fetchSpots,
    mapSearch,
    fetchMaps,
    filterMaps,
    mapCategoryFilter,
    mapDestinationFilter,
    spotCategoryFilter,
    spotDestinationFilter,
    mapCountries,
    mapCountriesFilter,
  }
}
